// @flow
import tinycolor from "tinycolor2";
import { generateTheme } from "@clds/theme-helper";
import { globalTheme, overrideTheme } from "@clds/component-theme";
import { appsTheme as typographyTheme } from "@clds/typography";
import { radioTheme } from "@clds/radio";
import { checkboxTheme } from "@clds/checkbox";
import { tooltipTheme } from "@clds/tooltip";
import { textFieldTheme } from "@clds/text-field";
import { iconTheme } from "@cld/icon";
import { theme as listItemLayoutTheme } from "@cld/list-item-layout";
import { theme as cardTheme } from "@cld/card";
import { getTextColorByBackgroundColor } from "utils/colorUtils";
import { formTheme, typographyConfig } from "@cld/form";
import { comboboxTheme } from "@cld/combobox";
import { theme as folderCardTheme } from "@cld/folder-card";
import { colorPickerTheme } from "@cld/color-picker";

// export for testing only
export const getAltColor = (color: string) => tinycolor(color).darken(20).toString();

const getContrastHighColor = (bgColor, lightColor, darkColor) => getTextColorByBackgroundColor(bgColor, lightColor, darkColor);

const getContrastInvertColor = (bgColor, lightColor, darkColor) => {
	const contrastHigh = getContrastHighColor(bgColor, lightColor, darkColor);

	return lightColor === contrastHigh ? darkColor : lightColor;
};

const convertPaletteToCldTheme = (uwPalette: Object) =>
	generateTheme({
		font: {
			size: {
				xxl: "4rem",
				xl: "3rem",
				lg: "2rem",
				md: "1.667rem",
				sm: "1.333rem",
				xs: "1.167rem",
				xxs: "1rem",
				xxxs: "0.84rem",
			},
		},
		spacing: {
			xxs: "0.5rem",
			xs: "0.75rem",
			sm: "1rem",
			md: "1.25rem",
			lg: "2rem",
			xl: "2.625rem",
			xxl: "3.5rem",
		},
		color: {
			menuIcons: uwPalette.menuIcons,
			windowBorder: uwPalette.windowBorder,
			sourceBg: uwPalette.sourceBg,
		},
		palette: {
			primary: uwPalette.link,
			error: uwPalette.error,
			success: uwPalette.complete,
			warning: uwPalette.action,
			background: uwPalette.sourceBg,
			backgroundAlt: getAltColor(uwPalette.sourceBg),
			surface: uwPalette.window,
			surfaceAlt: getAltColor(uwPalette.window),
			secondary: uwPalette.windowBorder,
			secondaryAlt: getAltColor(uwPalette.windowBorder),
			contrastHigh: getContrastHighColor(uwPalette.sourceBg, uwPalette.textLight, uwPalette.textDark),
			contrastLow: uwPalette.menuIcons,
			contrastInvert: getContrastInvertColor(uwPalette.sourceBg, uwPalette.textLight, uwPalette.textDark),
		},
		components: {
			...overrideTheme(listItemLayoutTheme, {
				md: {
					icon: iconTheme.lg,
					font: globalTheme.font.size.sm,
				},
			}),
			...overrideTheme(folderCardTheme, {
				border: {
					radius: 0,
				},
			}),
			...overrideTheme(cardTheme, {
				border: {
					radius: globalTheme.radius.lg,
				},
			}),
			skeleton: {
				base: uwPalette.sourceBg,
			},
			...overrideTheme(iconTheme, {
				xs: "0.666rem",
				sm: "1rem",
				md: "1.33rem",
				lg: "1.66rem",
				xl: "2.66rem",
				xxl: "3.5rem",
			}),
			...overrideTheme(typographyTheme, {
				xl: {
					size: "3rem",
					lineHeight: "3.25rem",
				},
				lg: {
					size: "1.5rem",
					lineHeight: "1.625rem",
				},
				md: {
					size: "1.125rem",
					lineHeight: "1.375rem",
				},
				sm: {
					size: "1rem",
					lineHeight: "1.25rem",
				},
				xs: {
					size: "0.92rem",
					lineHeight: "1.125rem",
				},
				xxs: {
					size: "0.84rem",
					lineHeight: "1rem",
				},
			}),
			...overrideTheme(tooltipTheme, {
				paper: {
					background: "rgba(0,0,0,0.8)",
					color: "#ffffff",
				},
			}),
			...overrideTheme(textFieldTheme, {
				background: {
					color: {
						base: globalTheme.color.white,
					},
				},
				input: {
					color: {
						base: globalTheme.color.black,
						placeholder: globalTheme.palette.secondaryAlt,
					},
					padding: {
						xs: "0.64rem",
					},
				},
			}),
			...overrideTheme(formTheme, {
				validation: {
					margin: {
						vertical: "0",
					},
				},
				label: {
					typography: typographyConfig("sm", "lowContrast"),
				},
			}),
			...overrideTheme(comboboxTheme, {
				background: {
					color: {
						base: uwPalette.window,
					},
				},
				clearIcon: {
					color: uwPalette.menuIcons,
				},
				dropdownIndicator: {
					color: uwPalette.menuIcons,
				},
				menu: {
					background: {
						color: uwPalette.window,
					},
				},
				border: {
					color: {
						base: uwPalette.windowBorder,
					},
				},
			}),
			...overrideTheme(colorPickerTheme, {
				item: {
					borderColor: "#90A0B3",
					sizeVariant: {
						xs: {
							width: "1.5rem",
							height: "1.5rem",
						},
						sm: {
							width: "2.062rem",
							height: "2.062rem",
						},
						md: {
							width: "2.812rem",
							height: "2.812rem",
						},
					},
				},
				palette: {
					itemSpacing: "0.3333rem",
					addIconSpacing: "0.14rem",
				},
			}),
			...overrideTheme(radioTheme, {
				size: "1rem",
				background: {
					color: {
						base: uwPalette.window,
						hover: uwPalette.window,
						focus: uwPalette.window,
						disabled: uwPalette.window,
					},
				},
				border: {
					color: {
						base: uwPalette.windowBorder,
					},
				},
			}),
			...overrideTheme(checkboxTheme, {
				width: "1.125rem",
				height: "1.125rem",
				icon: {
					color: {
						inactive: uwPalette.window,
						checked: uwPalette.window,
						disabled: globalTheme.palette.secondaryAlt,
						error: uwPalette.window,
					},
				},
				background: {
					color: {
						inactive: uwPalette.window,
					},
				},
				border: {
					color: {
						inactive: uwPalette.windowBorder,
					},
				},
			}),
		},
	});

export { convertPaletteToCldTheme };
